// Authored prose is stored as inline HTML straight out of contentEditable and
// later rendered on public pages for other people, so it's cleaned both when
// saved and when rendered: a short allowlist of inline tags, http(s)/mailto
// links only, and every attribute dropped except a link's href and an unfold
// anchor (`<span data-unfold="id">`, the words an unfold block hangs from).

// MARK is the author's highlight (the selection toolbar); it keeps no attributes
// but a margin note's anchor.
const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'CODE', 'MARK', 'A', 'BR'])
const UNFOLD_ANCHOR_RE = /^[A-Za-z0-9_-]{1,40}$/
const DROPPED = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH', 'IMG', 'VIDEO', 'AUDIO', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'])
const BLOCKISH = /^(DIV|P|LI|H[1-6]|BLOCKQUOTE|PRE|TR)$/

function copyInto(from: Node, to: Node, doc: Document): void {
  from.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      to.appendChild(doc.createTextNode(node.textContent ?? ''))
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as Element
    const tag = el.tagName.toUpperCase()
    if (DROPPED.has(tag)) return
    if (ALLOWED.has(tag)) {
      const clean = doc.createElement(tag.toLowerCase())
      // A highlight a margin note is about keeps the note's anchor (<mark data-note="id">).
      if (tag === 'MARK' && UNFOLD_ANCHOR_RE.test(el.getAttribute('data-note') ?? '')) {
        clean.setAttribute('data-note', el.getAttribute('data-note')!)
      }
      if (tag === 'A') {
        const href = (el.getAttribute('href') ?? '').trim()
        if (/^(https?:|mailto:)/i.test(href)) {
          clean.setAttribute('href', href)
          clean.setAttribute('rel', 'noopener noreferrer nofollow')
          clean.setAttribute('target', '_blank')
        }
      }
      copyInto(el, clean, doc)
      to.appendChild(clean)
      return
    }
    if (tag === 'SPAN' && UNFOLD_ANCHOR_RE.test(el.getAttribute('data-unfold') ?? '')) {
      const clean = doc.createElement('span')
      clean.setAttribute('data-unfold', el.getAttribute('data-unfold')!)
      copyInto(el, clean, doc)
      // An anchor with no words left is meaningless; drop the empty wrapper.
      if (clean.textContent) to.appendChild(clean)
      return
    }
    // Anything else is unwrapped; block-level wrappers (Chrome's <div> lines)
    // become line breaks so their text doesn't run together.
    if (BLOCKISH.test(tag) && to.childNodes.length > 0 && to.lastChild?.nodeName !== 'BR') {
      to.appendChild(doc.createElement('br'))
    }
    copyInto(el, to, doc)
  })
}

export function sanitizeInlineHtml(html: string): string {
  if (!html) return ''
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  const out = document.createElement('div')
  copyInto(tpl.content, out, document)
  return out.innerHTML.replace(/(<br>)+$/, '')
}

// ── Margin notes ─────────────────────────────────────────────────────────
// A margin note's words are plain text (MarginNoteBlock.text) and, once the
// author links some of them (Niki, 10/5), the same words as HTML
// (MarginNoteBlock.html) carrying only those links. Line breaks stay "\n" in
// the text, as in `text`, so the two always hold the same characters.

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Plain note text as markup (what `noteHtml` gives for words with no links). */
export function noteTextHtml(text: string): string {
  return escapeText(text)
}

function noteParts(from: Node, out: string[], inLink: boolean): void {
  from.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(escapeText(node.textContent ?? ''))
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const el = node as Element
    const tag = el.tagName.toUpperCase()
    if (DROPPED.has(tag)) return
    if (tag === 'BR') {
      out.push('\n')
      return
    }
    if (tag === 'A' && !inLink) {
      const href = (el.getAttribute('href') ?? '').trim()
      if (/^(https?:|mailto:)/i.test(href)) {
        const inner: string[] = []
        noteParts(el, inner, true)
        const words = inner.join('')
        if (words) out.push(`<a href="${escapeText(href).replace(/"/g, '&quot;')}">${words}</a>`)
        return
      }
    }
    // A line of its own (Chrome's <div> lines) starts on a new line.
    if (BLOCKISH.test(tag) && out.length > 0 && !out[out.length - 1].endsWith('\n')) out.push('\n')
    noteParts(el, out, inLink)
  })
}

/**
 * A margin note's words as stored HTML: text and http(s)/mailto links, and
 * nothing else (no formatting, no attribute but href). Given its own output it
 * returns it unchanged.
 */
export function noteHtml(raw: string): string {
  if (!raw) return ''
  const tpl = document.createElement('template')
  tpl.innerHTML = raw
  const out: string[] = []
  noteParts(tpl.content, out, false)
  return out.join('')
}

/** The plain words of a note's HTML (what its `text` holds). */
export function noteHtmlText(html: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = noteHtml(html)
  return tpl.content.textContent ?? ''
}

/** Whether a note's HTML carries a link (without one, only `text` is kept). */
export function noteHasLink(html: string): boolean {
  return /<a\s/i.test(html)
}

// ── Pull quote attributions ──────────────────────────────────────────────
// Who said a pull quote (PullQuoteBlock.attribution) is plain text the
// author types under it. The reader adds the dash, so one typed in front is
// dropped; save_writing_quote() reads it the same way (the v2.4 migration).

/** The longest attribution kept, in characters. */
export const MAX_ATTRIBUTION = 160

/** A pull quote's attribution as stored: one line, no leading dash, trimmed; undefined when there's none. */
export function cleanAttribution(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const s = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—―~]+/, '')
    .slice(0, MAX_ATTRIBUTION)
    .trim()
  return s || undefined
}
